import {
  Controller,
  type UseFieldArrayRemove,
  type UseFieldArraySwap,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { StyleSheet } from 'react-native-unistyles';
import {
  attributeTypeLabel,
  type BadgeEditFormValues,
} from '@/components/Badge/BadgeEdit/BadgeEdit.types';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import Card from '@/components/UI/Card';
import Checkbox from '@/components/UI/Checkbox';
import Row from '@/components/UI/Row';
import Select from '@/components/UI/Select';
import Text from '@/components/UI/Text';
import TextInput from '@/components/UI/TextInput';
import useConfig from '@/hooks/useConfig';
import pluralize from '@/utils/pluralize';
import { getCsvValues } from './BadgeEdit.utils';

const styles = StyleSheet.create((theme) => ({
  attributeHeaderRow: {
    backgroundColor: theme.colors.contrastStripBackground,
  },
  attributeInput: {
    marginRight: theme.space('xs'),
  },
  attributeTitleText: {
    color: theme.colors.primary,
    fontWeight: theme.fontWeight.bold,
  },
}));
function BadgeEditAttribute({
  index,
  remove,
  swap,
  attributeCount,
}: {
  index: number;
  remove: UseFieldArrayRemove;
  swap: UseFieldArraySwap;
  attributeCount: number;
}) {
  const validateOptions = (
    value: string | null | undefined,
    formValues: BadgeEditFormValues,
    optionLimit: number,
  ) => {
    if (!value) return true;
    const valuesArray = getCsvValues(value);
    if (valuesArray.length > optionLimit) {
      return `Cannot have more than ${optionLimit} options`;
    }
    const attribute = formValues.badge_attributes?.[index];
    if (attribute?.attribute_type === 'numeric') {
      for (const item of valuesArray) {
        if (Number.isNaN(Number(item))) {
          return `Non-Number not allowed: ${item}`;
        }
      }
    }
    const seen = new Set<string>();
    for (const item of valuesArray) {
      if (seen.has(item)) {
        return `Duplicates not allowed: ${item}`;
      }
      seen.add(item);
    }
    return true;
  };
  const { control, trigger } = useFormContext<BadgeEditFormValues>();
  const { badge_attribute_option_max } = useConfig();
  const attributeOptionMax = Number.parseInt(
    badge_attribute_option_max || '',
    10,
  );
  const attributeType = useWatch({
    control,
    name: `badge_attributes.${index}.attribute_type`,
  });
  return (
    <Card>
      <Row spaced style={styles.attributeHeaderRow}>
        <Box direction="vertical" space="sm" flex>
          <Box direction="vertical">
            <Controller
              rules={{
                required: {
                  value: true,
                  message: 'Required',
                },
              }}
              name={`badge_attributes.${index}.title`}
              control={control}
              render={({ field, fieldState: { error } }) => {
                return (
                  <>
                    <Text
                      size="xs"
                      variant={error?.type ? 'error' : 'typographySecondary'}
                    >
                      {error?.message || 'Title'}
                    </Text>
                    <TextInput
                      containerStyle={styles.attributeInput}
                      flex
                      variant="underlined"
                      style={styles.attributeTitleText}
                      onChangeText={field.onChange}
                      autoCapitalize="words"
                      enterKeyHint="done"
                      value={field.value}
                      placeholder="Enter Title..."
                      maxLength={400}
                      testID={`BadgeEditAttributeTitle-${index}`}
                    />
                  </>
                );
              }}
            />
          </Box>
          <Box direction="vertical">
            <Controller
              rules={{
                validate: (value, formValues) =>
                  validateOptions(value, formValues, attributeOptionMax),
              }}
              name={`badge_attributes.${index}.options_csv`}
              control={control}
              render={({ field, fieldState: { error } }) => {
                const values = getCsvValues(field.value || '');
                const numberOfChoices = values.length || 0;
                const label = `${numberOfChoices > 0 ? `${numberOfChoices} ${pluralize('Choice', numberOfChoices)}` : 'Choices - Optional'}`;
                return (
                  <>
                    <Text
                      size="xs"
                      variant={error?.message ? 'error' : 'typographySecondary'}
                      numberOfLines={1}
                    >
                      {error?.message || label}
                    </Text>
                    <TextInput
                      containerStyle={styles.attributeInput}
                      flex
                      variant="underlined"
                      onChangeText={(text) => {
                        if (text.startsWith(',')) {
                          field.onChange(text);
                          return;
                        }
                        if (text.endsWith(',')) {
                          field.onChange(text.trim());
                          return;
                        }
                        if (text.endsWith(' ')) {
                          field.onChange(text.trimStart());
                          return;
                        }
                        const newValue = getCsvValues(text).join(',');
                        field.onChange(newValue);
                        return;
                      }}
                      maxLength={1000}
                      autoCapitalize="words"
                      enterKeyHint="done"
                      value={field.value || ''}
                      testID={`BadgeEditAttributeOptions-${index}`}
                      placeholder={
                        attributeType === 'text'
                          ? 'Blank or A,B,C...'
                          : 'Blank or 1,2,3...'
                      }
                    />
                  </>
                );
              }}
            />
          </Box>
        </Box>
        <Box direction="vertical" space="lg" align="flex-end">
          <Controller
            rules={{
              required: true,
            }}
            name={`badge_attributes.${index}.attribute_type`}
            control={control}
            render={({ field }) => {
              return (
                <Select
                  width={160}
                  onValueChange={(value) => {
                    field.onChange(value);
                    trigger(`badge_attributes.${index}.options_csv`);
                  }}
                  value={field.value}
                  placeholder="Select Type..."
                  options={[
                    {
                      label: attributeTypeLabel.text,
                      value: 'text',
                    },
                    {
                      label: attributeTypeLabel.numeric,
                      value: 'numeric',
                    },
                  ]}
                />
              );
            }}
          />
          <Controller
            name={`badge_attributes.${index}.is_required`}
            control={control}
            render={({ field }) => {
              return (
                <Checkbox
                  value={field.value}
                  onValueChange={(value) => field.onChange(value)}
                  accessibilityLabel="Required"
                >
                  <Text bold>Required</Text>
                </Checkbox>
              );
            }}
          />

          <Box direction="horizontal" space="sm">
            {attributeCount > 1 ? (
              <ActionButton
                onPress={() => {
                  swap(index, index - 1);
                }}
                disabled={index === 0}
                variant="secondary"
                icon="arrow-up-outline"
              />
            ) : null}
            {attributeCount > 1 ? (
              <ActionButton
                onPress={() => {
                  swap(index, index + 1);
                }}
                disabled={index === attributeCount - 1}
                variant="secondary"
                icon="arrow-down-outline"
              />
            ) : null}

            <ActionButton
              onPress={() => {
                remove(index);
              }}
              variant="secondary"
              icon="close-outline"
            />
          </Box>
        </Box>
      </Row>
    </Card>
  );
}
export default BadgeEditAttribute;
